import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Row as TanTableRow, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import type { FirmsController } from "../../../api/src/firms/firms.controller";
import type {
  SecurityAllocationRequest,
  SecurityCategoryAssetClass,
  SecurityCategoryAssetClassColumns,
} from "../../../api/src/firms/firms.service";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import TabContainerWithTabs from "../components/TabContainer";
import EditableTableRow from "../components/Table/EditableTableRow";
import { Table, useTable } from "../components/Table/Table";
import { textOrExactFilter } from "../components/Table/filters";
import {
  deserializeDate,
  useAuthenticatedMutationAsync,
  useAuthenticatedQuery,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import { permissionAwareErrorNotification } from "../lib/errorNotifications";
import { processFileResponse } from "../lib/file";
import { formatPercent } from "../lib/numbers";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import FirmNav from "./FirmNav";
import { securityCategoryAssetClassColumnsMap } from "./lib";

function useQueryExportCategoryAssetClasses() {
  return useAuthenticatedQuery<
    UnpackResponse<FirmsController["exportSecurityCategoriesAssetClasses"]>,
    void
  >({
    queryKey: ["firm", "security-category-asset-classes-allocations", "export"],
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}

/**
 * The route this screen READS the firm's security-category asset classes from.
 *
 * Unversioned. APP-1563 paginated the route and moved it to `/v1`; on
 * 2026-09-29 the operator removed it from version 1 of the Firm API, so it
 * answers here again, for the console only
 * (`docs/adr/firm-domain-api-version-1.md`). It is also the FIRST element of
 * every react-query key `useAuthenticatedFetchAllPages` uses, so the
 * invalidation after a save reaches every page of the collection rather than
 * only the one whose url it happens to spell.
 */
const SECURITY_CATEGORY_ASSET_CLASSES_PATH =
  "/firm/security-category-asset-classes";

/**
 * The route this screen WRITES the collection through — a DIFFERENT path from
 * the read, ending `-allocations`, with no `GET` of its own.
 *
 * The asymmetry is recorded in `docs/adr/firm-domain-admission.md` and was not
 * APP-1563's to reconcile: that job moved both handlers to `/v1` and renamed
 * neither, and both left `/v1` together on 2026-09-29. Two constants rather
 * than one because there are genuinely two paths; collapsing them would be the
 * rename.
 *
 * The EXPORT is a third matter and is unversioned too:
 * `an-decision-01a088c0c2b38187` keeps the `StreamableFile` exports out of the
 * JSON API, so `GET /firm/security-category-asset-classes-allocations/export`
 * never moved. `useAuthenticatedQuery` builds that url out of its
 * react-query key, which is why the key above is left exactly as it stands.
 */
const SECURITY_CATEGORY_ASSET_CLASSES_WRITE_PATH =
  "/firm/security-category-asset-classes-allocations";

/** What the read answers with: a page of rows and the pagination beside it. */
type CategoryAssetClassesResponse = SerializedObject<
  UnpackResponse<FirmsController["getSecurityCategoriesAssetClasses"]>
>;

type SecurityCategoryAssetClassRow = SecurityCategoryAssetClass;

const CategoryAssetClass = () => {
  const roles = useRoles();
  const notificationContext = useContext(NotificationContext);

  // keeps Rows edit details in form of <Category_assetClass_allocation, value>
  const [fields, setFields] = useState<Record<string, number>>({});

  // keeps track of security allocations along with the changes made in form of
  // [{securityCategory: "category", usLargeCapEquity: {allocation: 10, isOverride: false}, updatedTime: "2021-10-10T00:00:00.000Z"}]
  const [securityAllocationsEdited, setCategoryAssetAllocation] = useState<
    SecurityCategoryAssetClass[]
  >([]);

  // keeps track of changed security allocations to submit to database for update in form of
  // {securityCategory: "category", mode: insertUpdate, usLargeCapEquity: 10}
  const [changedSecurityAllocations, setChangedSecurityAllocations] = useState<
    Record<string, SecurityAllocationRequest>
  >({});

  const columnHelper = useMemo(
    () => createColumnHelper<SecurityCategoryAssetClassRow>(),
    [],
  );

  // 1. get all security categories with asset class
  //
  // The route answers a PAGE, not the whole collection (APP-1563), so one
  // response is never the answer on its own: the walk in
  // `useAuthenticatedFetchAllPages` keeps asking until it has as many rows as
  // the sibling key says the firm has. Nothing in `tsc` can catch getting this
  // wrong — the element type of a page and of the whole array are the same — so
  // `SecurityCategoryAssetClass.pagination.spec.tsx` is what holds it.
  const {
    rows: collectedRows,
    hasEveryRow: hasEveryCategoryAssetClass,
    isPendingFirstPage: isPendingFirstCategoryAssetClassesPage,
    reload: reloadCategoryAssetClasses,
  } = useAuthenticatedFetchAllPages<
    CategoryAssetClassesResponse["data"][number]
  >(SECURITY_CATEGORY_ASSET_CLASSES_PATH);

  const securityAssetAllocations = useMemo(() => {
    return collectedRows.map((securityCategoryAssetAllocation) => ({
      ...securityCategoryAssetAllocation,
      updatedTime: deserializeDate(securityCategoryAssetAllocation.updatedTime),
    }));
  }, [collectedRows]);

  // 2. create asset class columns for each security category
  const assetClassColumns = Array.from(
    securityCategoryAssetClassColumnsMap.keys(),
  ) as SecurityCategoryAssetClassColumns[];

  // 3. define onChange for each cell
  const onChangeText = useCallback(
    (cellId: string, rowId: string, value: string) => {
      const updatedValue = +value;
      setFields({
        ...fields,
        [`${cellId}_allocation`]: updatedValue,
      });
    },
    [fields],
  );

  // 4. create columns for table
  const columns = useMemo(() => {
    const dynamicColumns = assetClassColumns.map((key) =>
      columnHelper.accessor(key, {
        header: () => securityCategoryAssetClassColumnsMap.get(key)?.label,
        cell: (info) => `${formatPercent(info.getValue().allocation / 100, 2)}`,
        enableColumnFilter: false,
        meta: {
          editableComponent: (cell) => (
            <InputGroup style={{ width: "115px" }}>
              <Form.Control
                type="number"
                value={fields[cell.id + "_allocation"]}
                onChange={(e) => {
                  return onChangeText(cell.id, cell.row.id, e.target.value);
                }}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          ),
        },
      }),
    );
    return [
      columnHelper.accessor("securityCategory", {
        header: () => "Category",
        // A link from a security's page sets `?category=` (APP-1486).
        filterFn: textOrExactFilter,
      }),
      ...dynamicColumns,
      columnHelper.accessor("updatedTime", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Updated Time",
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        size: 120,
      }),
    ];
  }, [assetClassColumns, columnHelper, fields, onChangeText]);

  // 5. onEditRow to update state of fields in memory with new values of columns
  const onEditRow = useCallback(
    (row: TanTableRow<SecurityCategoryAssetClassRow>) => {
      const securityAllocationsMap = new Map(
        securityAllocationsEdited.map((item) => [item.securityCategory, item]),
      );

      const currentRow = securityAllocationsMap.get(
        row.original.securityCategory,
      );

      if (typeof currentRow === "undefined") {
        throw new Error("Row not found in table: ", {
          cause: row?.id,
        });
      }

      const currentColumnValues: Record<string, number> = {};

      for (const key of assetClassColumns) {
        const value = currentRow[key as keyof SecurityCategoryAssetClass];
        if (
          typeof value === "object" &&
          value !== null &&
          "allocation" in value
        ) {
          currentColumnValues[`${row.id}_${key}_allocation`] = value.allocation;
        }
      }
      setFields({
        ...fields,
        ...currentColumnValues,
      });
    },
    [securityAllocationsEdited, fields, assetClassColumns],
  );

  // 6. Each row update that handles updating state of ui object and update the database to update the security allocations
  const onUpdateAssetAllocation = useCallback(
    (security: string, assetClassFields: Record<string, number>) => {
      setCategoryAssetAllocation(
        securityAllocationsEdited.map((securityAllocation) => {
          // work with current security edit
          if (securityAllocation.securityCategory !== security) {
            return securityAllocation;
          }

          // If total is not 100, return to original state
          const sum = Object.values(assetClassFields).reduce(
            (a, b) => a + b,
            0,
          );
          if (sum !== 100) {
            notificationContext.pushNotification({
              id: "asset-allocations",
              header: `Check total for ${security} Asset Allocations`,
              body: "Firm Asset Allocations do Not sum to 100 for the selected category",
              variant: "danger",
            });
            return securityAllocation;
          }

          // current state is same as default allocations for all asset classes
          const areAllDefaultAllocationsMatching = assetClassColumns.every(
            (column) => {
              const securityAllocationValue =
                securityAllocation[column]?.defaultAllocation;
              const assetClassFieldValue =
                assetClassFields[`${security}_${column}_allocation`];
              return securityAllocationValue === assetClassFieldValue;
            },
          );

          // current state is same as database (default or override) allocations for all asset classes
          const areAllOriginalAllocationsMatching = assetClassColumns.every(
            (column) => {
              const securityAllocationValue =
                securityAllocation[column]?.originalAllocation;
              const assetClassFieldValue =
                assetClassFields[`${security}_${column}_allocation`];
              return securityAllocationValue === assetClassFieldValue;
            },
          );

          // update db state values : if no changes are made compared to db values, just remove from update list
          if (areAllOriginalAllocationsMatching) {
            setChangedSecurityAllocations((changedSecurityAllocations) => {
              delete changedSecurityAllocations[security];
              return { ...changedSecurityAllocations };
            });
          } else if (areAllDefaultAllocationsMatching) {
            // update db state values : if all default allocations are matching, We need to delete from override table
            setChangedSecurityAllocations((changedSecurityAllocations) => ({
              ...changedSecurityAllocations,
              [security]: {
                mode: "DELETE",
              },
            }));
          } else {
            // update db state values : Insert or update the security allocation
            setChangedSecurityAllocations((changedSecurityAllocations) => ({
              ...changedSecurityAllocations,
              [security]: {
                mode: "INSERTUPDATE",
                ...assetClassColumns.reduce(
                  (
                    acc: Record<SecurityCategoryAssetClassColumns, number>,
                    column,
                  ) => {
                    acc[column] =
                      assetClassFields[`${security}_${column}_allocation`];
                    return acc;
                  },
                  {} as Record<SecurityCategoryAssetClassColumns, number>,
                ),
              },
            }));
          }

          function isSecurityCategoryAssetClassColumns(
            key: string,
          ): key is SecurityCategoryAssetClassColumns {
            return assetClassColumns.includes(
              key as SecurityCategoryAssetClassColumns,
            );
          }

          const keysToUse = Object.keys(securityAllocation).filter(
            isSecurityCategoryAssetClassColumns,
          );

          // update the security allocation with new values
          return keysToUse.reduce((result, key) => {
            const assetClassFieldKey = `${securityAllocation.securityCategory}_${key}_allocation`;
            const assetClassFieldValue = assetClassFields[assetClassFieldKey];
            return {
              ...result,
              [key]: {
                ...securityAllocation[key],
                allocation: assetClassFieldValue,
                isOverride:
                  assetClassFieldValue !==
                  securityAllocation[key].defaultAllocation,
              },
            };
          }, securityAllocation);
        }),
      );
    },
    [assetClassColumns, notificationContext, securityAllocationsEdited],
  );

  // 7. Reset Row
  const originalAssetClassValues = useMemo(() => {
    function isSecurityCategoryAssetClassColumns(
      key: string,
    ): key is SecurityCategoryAssetClassColumns {
      return assetClassColumns.includes(
        key as SecurityCategoryAssetClassColumns,
      );
    }

    return securityAssetAllocations.reduce(
      (result, securityCategoryAssetAllocation) => {
        const keysToUse = Object.keys(securityCategoryAssetAllocation).filter(
          isSecurityCategoryAssetClassColumns,
        );
        return {
          ...result,
          ...keysToUse.reduce(
            (result, key) => ({
              ...result,
              [`${securityCategoryAssetAllocation.securityCategory}_${key}_allocation`]:
                securityCategoryAssetAllocation[key].defaultAllocation,
            }),
            {},
          ),
        };
      },
      {},
    );
  }, [assetClassColumns, securityAssetAllocations]);

  const onResetRow = useCallback(
    (row: TanTableRow<SecurityCategoryAssetClassRow>) => {
      const category = row.original.securityCategory;

      onUpdateAssetAllocation(
        category,
        Object.entries(originalAssetClassValues)
          .filter(([key]) => key.startsWith(`${category}_`))
          .reduce(
            (acc, [key, value]) => {
              if (typeof value === "number") {
                return { ...acc, [key]: value };
              }
              return acc;
            },
            {} as Record<string, number>,
          ),
      );
    },
    [onUpdateAssetAllocation, originalAssetClassValues],
  );

  const hasAssetClassOverride = useMemo(() => {
    return (category: string) => {
      return assetClassColumns.some(
        (column) =>
          securityAllocationsEdited.find(
            (securityAllocation) =>
              securityAllocation.securityCategory === category,
          )?.[column]?.isOverride,
      );
    };
  }, [assetClassColumns, securityAllocationsEdited]);

  // 8. On Discard Row reset the fields to original values
  const onDiscardRow = useCallback(
    (row: TanTableRow<SecurityCategoryAssetClassRow>) => {
      const category = row.original.securityCategory;
      setFields({
        ...fields,
        ...Object.entries(originalAssetClassValues)
          .filter(([key]) => key.startsWith(`${category}_`))
          .reduce(
            (acc, [key, value]) => {
              if (typeof value === "number") {
                return { ...acc, [key]: value };
              }
              return acc;
            },
            {} as Record<string, number>,
          ),
      });
    },
    [fields, originalAssetClassValues],
  );

  // 9. Confirm Row Update
  const onConfirmRow = useCallback(
    (row: TanTableRow<SecurityCategoryAssetClassRow>) => {
      const rowFields = Object.keys(fields).reduce(
        (result, key) => {
          if (key.startsWith(`${row.id}_`)) {
            result[key] = fields[key];
          }
          return result;
        },
        {} as { [key: string]: number },
      );
      onUpdateAssetAllocation(row.id, rowFields);
    },
    [onUpdateAssetAllocation, fields],
  );

  // 10. Handle Discard Changes (Database Submit / Discard)
  const handleDiscardChanges = useCallback(() => {
    setCategoryAssetAllocation(securityAssetAllocations);
    setChangedSecurityAllocations({});
  }, [securityAssetAllocations]);

  // 11. Submit Changes to Database
  const queryClient = useQueryClient();

  const updateAssetAllocations = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<FirmsController["updateSecurityCategoriesAssetClasses"]>
    >
  >(
    SECURITY_CATEGORY_ASSET_CLASSES_WRITE_PATH,
    async (data: Record<string, SecurityAllocationRequest>) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );
  const onSubmit = useMutation({
    mutationFn: async () => {
      try {
        await updateAssetAllocations(changedSecurityAllocations);
        // The write echoes a PAGE bounded by its own request (APP-1563), not
        // the collection, so writing the echo into the read's cache as the
        // whole list would present one page as the whole collection the moment
        // the firm has more security categories than a page.
        //
        // It never reached the read's cache in any case: the key it was written
        // under was `["/firm/security-category-asset-classes-allocations"]`,
        // the WRITE's path, while the read's key was
        // `["/firm/security-category-asset-classes"]` — two different paths for
        // one collection, so the entry it wrote was one nothing read. The path
        // is invalidated instead, which matches every page's key, and the pages
        // already collected are dropped: the walk stops once it holds them all,
        // so invalidating alone would refetch nothing.
        queryClient.invalidateQueries({
          queryKey: [SECURITY_CATEGORY_ASSET_CLASSES_PATH],
        });
        reloadCategoryAssetClasses();
        setChangedSecurityAllocations({});
        notificationContext.pushNotification({
          id: "asset-allocations",
          header: "Asset Allocations Updated",
          body: "Firm Asset allocation settings updated",
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save Asset allocations details", err);
        // APP-1327: a 403 means the user lacks the Admin role required to save
        // this mapping — show a clear permissions message. Any other failure
        // keeps the generic copy so real errors are not masked as a permissions
        // problem.
        notificationContext.pushNotification(
          permissionAwareErrorNotification(
            err,
            "asset-allocations",
            "Failed to Save Asset Allocations",
            "Firm Asset allocation settings not saved",
            "Asset Allocations",
          ),
        );
      }
    },
  });

  useEffect(() => {
    const setFieldsFromSecurityAllocation = () => {
      const newFields: Record<string, number> = {};

      securityAssetAllocations.forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (key !== "securityCategory" && key !== "updatedTime") {
            const typedKey = key as SecurityCategoryAssetClassColumns;
            const newKey = `${item.securityCategory}_${typedKey}_allocation`;
            newFields[newKey] = item[typedKey].allocation;
          }
        });
      });

      setFields(newFields);
    };
    // Copied into the editable state only once the walk HOLDS every row. A copy
    // per page would hand the screen a partial collection to edit, and the
    // `fields` map built beside it would be missing the rows still outstanding
    // — so an Admin could open a row that the next page was about to replace.
    if (hasEveryCategoryAssetClass) {
      // on load, set the category allocations and fields
      setCategoryAssetAllocation(securityAssetAllocations);
      setFieldsFromSecurityAllocation();
    }
  }, [hasEveryCategoryAssetClass, securityAssetAllocations]);

  // `?category=` shows only that category's row (APP-1486).
  const [searchParams] = useSearchParams();
  const initialColumnFilters = useMemo(() => {
    const category = searchParams.get("category");
    return category ? [{ id: "securityCategory", value: [category] }] : [];
  }, [searchParams]);

  const { table } = useTable({
    columns: columns,
    data: securityAllocationsEdited,
    getRowId: (row) => row.securityCategory,
    initialState: {
      pagination: {
        pageSize: 20,
      },
      columnFilters: initialColumnFilters,
    },
    autoResetAll: false,
  });

  const { isLoading: isLoadingExport, refetch: refetchExport } =
    useQueryExportCategoryAssetClasses();

  const onExport = useCallback(async () => {
    await refetchExport();
  }, [refetchExport]);

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      <Row>
        <Col>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Export"
                icon="/icons/folded-list.svg"
                onClick={onExport}
                disabled={isLoadingExport}
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row
        hidden={Object.keys(changedSecurityAllocations).length <= 0}
        className="mb-1"
      >
        <Col className="d-grid gap-1" xl={6} md={12}>
          <Button
            className="mb-1"
            variant="success"
            onClick={() => onSubmit.mutateAsync()}
            disabled={onSubmit.isPending}
          >
            Submit{" "}
            {onSubmit.isPending ? <Spinner animation="border" size="sm" /> : ""}
          </Button>
        </Col>
        <Col className="d-grid gap-1" xl={6} md={12}>
          <Button
            className="mb-1"
            variant="danger"
            onClick={handleDiscardChanges}
            disabled={onSubmit.isPending}
          >
            Discard
          </Button>
        </Col>
      </Row>
      <Row>
        <Col style={{ pointerEvents: onSubmit.isPending ? "none" : "auto" }}>
          {/* Only while the FIRST page is outstanding. Every further page turns
              the hook pending again under a new key, and unmounting the table
              for each of them would flash the screen through Loading once per
              page. */}
          {isPendingFirstCategoryAssetClassesPage ? (
            <Loading />
          ) : (
            <Table
              table={table}
              RowComp={roles.includes("Admin") ? EditableTableRow : undefined}
              onSave={onConfirmRow}
              onEdit={onEditRow}
              onDiscard={onDiscardRow}
              editComp={(row) => (
                <ActionButton
                  type="button"
                  onClick={() => onResetRow(row)}
                  disabled={
                    !hasAssetClassOverride(row.original.securityCategory)
                  }
                  variant="icon"
                  label="Reset"
                  icon="/icons/redo.svg"
                />
              )}
            />
          )}
        </Col>
      </Row>
    </TabContainerWithTabs>
  );
};

export default CategoryAssetClass;
